import React from "react";
import { PinnedActionBar } from "../../../components/ui";

export interface PinnedCtaBarProps {
  /**
   * The page's own gutter taken back out, and put back inside.
   *
   * The blur runs the full width of the screen, so the bar has to cancel
   * exactly the padding of whatever shell it is standing in — and put that
   * padding back inside, so the button still lines up with the cards above it.
   * The negative bottom margin in there is the page's last padding taken off,
   * so the bar reaches the bottom of the screen. Each shell knows its own
   * numbers: the checkout is in `AppLayout`'s panel, the trial steps are in
   * `TrialLayout`.
   *
   * A `floating` bar cancels nothing — it is already the width of the window
   * and already on its bottom edge — so there it is only the gutter, kept as
   * padding. See `ctaGutter` on the price list.
   */
  className?: string;
  /**
   * Hold the bar against the window rather than the foot of the column it was
   * written in. See `floats` on `PinnedActionBar`, which is where the two are
   * weighed up; the price list is the one screen here that needs it.
   */
  floating?: boolean;
  children: React.ReactNode;
}

/**
 * The buy flow's pinned bar: the app's `PinnedActionBar` with the padding this
 * flow's single, full-width button wants.
 *
 * The glass, the blur and the reason for both are the shared component's; what
 * is here is the measurements. Picking a plan, paying for one, starting a
 * trial and landing on the confirmation are four screens of one job, and the
 * button has to sit in exactly the same place on each — so the numbers live
 * once, here, rather than four times in four pages.
 *
 * `bg-white` is the surface on a desktop and the fallback on a phone that
 * cannot blur; see `PinnedActionBar`, which is where that doubling-up is
 * explained. Only the trial steps and the price list ever draw this above
 * `md`, and there it is the plain white footer every form in the app wears.
 *
 * It claims the phone's bottom edge. Unlike the long forms elsewhere — where
 * the form itself claims it and holds it for as long as it is open — the four
 * screens of this flow have nothing else that knows, so the bar is the thing
 * that mounts and the thing that claims.
 *
 * The room under the button is the room the tab bar was keeping: 20px over the
 * safe-area inset, matching every other bar in the app that has taken this
 * edge. The inset is 0px on a phone reporting none, which is the case a bar
 * like this would otherwise land on the home indicator in.
 */
const PinnedCtaBar: React.FC<PinnedCtaBarProps> = ({
  className = "",
  floating = false,
  children,
}) => (
  <PinnedActionBar
    claimsBottomEdge
    floats={floating}
    className={
      "bg-white pt-3 pb-[calc(env(safe-area-inset-bottom)_+_1.25rem)] " +
      className
    }
  >
    {children}
  </PinnedActionBar>
);

export default PinnedCtaBar;
